/**
 * Finance tab — master-detail layout (2026-06 redesign).
 *
 * Replaces the previous 16-column wide table with:
 *   • `FinanceKpiBar`        — sticky project KPIs (turnover emphasised).
 *   • `DesignationList`      — compact list, collapsible per section.
 *                              Key columns only: name, status, ordered,
 *                              invoiced, declared progress (quick-edit),
 *                              hours spent/sold ratio (R&D).
 *   • `DesignationDetailPanel` — opens on row click (inline ≥md,
 *                              Drawer below). All other fields live
 *                              here, click-to-edit with immediate
 *                              `updateDoc`, plus the invoicing block
 *                              (≤5 deposits, already-invoiced/%/RI).
 *
 * This container keeps every Firestore subscription, the permission
 * gates and the single click-to-edit implementation (`renderCell`)
 * so the write rules stay mirrored in exactly one place. The margins
 * panel was removed from this page (component kept in the repo).
 */

import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router';
import { serverTimestamp } from 'firebase/firestore';
import type { Designation, HourlyRate, Invoice } from '@altyor-kronos/shared-types';
import { DESIGNATION_STATUSES } from '@altyor-kronos/shared-types';
import { useAuth } from '../../core/auth/useAuth';
import {
  canWriteDesignationFields,
  canManageDesignations,
  groupDesignationsBySection,
  parseDesignationForm,
  type DesignationFormValues,
} from './designations';
import DesignationDialog, { type ExistingDesignation } from './DesignationDialog';
import ConfirmDeleteDialog from '../admin/ConfirmDeleteDialog';
import { useCollectionSnapshot } from '../../core/data/useCollectionSnapshot';
import {
  createDesignationHeader,
  createDesignationHeaders,
  createInvoice,
  deleteDesignation,
  deleteInvoice,
  updateDesignationFields,
  updateDesignationHeader,
  updateInvoice,
} from '../../core/data/projectMutations';
import { useProjectDesignations } from '../kronos/useKronosData';
import { useProjectInvoices } from './useProjectInvoices';
import { useClientDoc } from './useClientDoc';
import type { ProjectDetailContext } from './ProjectOverview';
import { colors } from '../../core/theme/theme';
import { formatNumber, projectSummary } from './finance';
import { sumInvoicesInEUR } from './invoiceMath';
import InvoiceDialog, { type ExistingInvoice } from './InvoiceDialog';
import FinanceKpiBar from './FinanceKpiBar';
import DesignationList from './DesignationList';
import DesignationDetailPanel, {
  type EditableField,
  type RenderCell,
} from './DesignationDetailPanel';
import type { InvoiceRow } from './InvoicesBlock';

interface ActiveEdit {
  designationId: string;
  field: EditableField;
  value: string;
}

/** Mirrors Clockify's "Show all / Active / Done" task filter. */
type ArchiveFilter = 'active' | 'all' | 'archived';
const ARCHIVE_FILTERS: readonly ArchiveFilter[] = ['active', 'all', 'archived'];

const STATUS_OPTIONS = DESIGNATION_STATUSES;

export default function FinanceTable() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { project } = useOutletContext<ProjectDetailContext>();
  const projectId = project.id;

  // Designations subscription — same as Phase 5.
  const { designations: rows, loading, error } = useProjectDesignations(projectId);

  // ── Permissions ───────────────────────────────────────────────
  const canManage = user
    ? canManageDesignations(user.roles, user.uid, project.team?.managers ?? [])
    : false;
  // A wider audience than the full-edit gate: direction + admin get a
  // field-level write restricted to `progress` and `isArchived`.
  const canWriteFields = user
    ? canWriteDesignationFields(user.roles, user.uid, project.team?.managers ?? [])
    : false;
  // Invoices CRUD is tighter: only finance per firestore.rules.
  const canManageInvoices = user?.roles.includes('finance') ?? false;
  // Invoice READ is finance / direction / admin / project manager per
  // firestore.rules — the invoiced column + block follow the same gate.
  const canSeeInvoices =
    user?.roles.includes('finance') ||
    user?.roles.includes('direction') ||
    user?.roles.includes('admin') ||
    (user ? (project.team?.managers?.includes(user.uid) ?? false) : false);

  // Invoices — subscribe only for roles the rules allow to read.
  const { rows: invoices } = useProjectInvoices(projectId, Boolean(canSeeInvoices));

  // Hourly rates — for the rate row in the detail panel.
  const { rows: rateRows } = useCollectionSnapshot<HourlyRate>('hourlyRates');
  // Client subscription — for the rate resolution context.
  const { client } = useClientDoc(project.clientId);

  // Archive filter — display only. The Clockify import left ~4300 zero-hour
  // task lines on the projects; archiving hides them from the time-entry
  // pickers and, by default, from this list. It never touches the totals:
  // `summary` below still reads every row.
  const [archiveFilter, setArchiveFilter] = useState<ArchiveFilter>('active');

  const visibleRows = useMemo(
    () =>
      rows.filter(({ data }) =>
        archiveFilter === 'all' ? true : Boolean(data.isArchived) === (archiveFilter === 'archived'),
      ),
    [rows, archiveFilter],
  );
  const archivedCount = useMemo(() => rows.filter((r) => r.data.isArchived).length, [rows]);

  const groups = useMemo(
    () => groupDesignationsBySection(visibleRows.map((r) => r.data)),
    [visibleRows],
  );
  // Uniqueness spans every designation, archived included — an archived name
  // still occupies the slot, otherwise re-adding it creates a hidden twin.
  const existingForUniqueness = useMemo(
    () => rows.map((r) => ({ id: r.id, name: r.data.name })),
    [rows],
  );
  const summary = useMemo(() => projectSummary(rows.map((r) => r.data)), [rows]);

  // Per-row invoiced totals in EUR from the live subscription.
  const invoicedByDesignation = useMemo(() => {
    const grouped = new Map<string, Invoice[]>();
    for (const r of invoices) {
      const bucket = grouped.get(r.data.designationId);
      if (bucket) bucket.push(r.data);
      else grouped.set(r.data.designationId, [r.data]);
    }
    const m = new Map<string, number>();
    for (const [id, list] of grouped) {
      m.set(id, sumInvoicesInEUR(list));
    }
    return m;
  }, [invoices]);

  // ── Selection (detail panel) ──────────────────────────────────
  // Derived from the live rows: if the selected designation vanishes
  // (deleted elsewhere), the panel closes by itself.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = selectedId
    ? rows.find((r) => r.id === selectedId)?.data ?? null
    : null;
  const selectedInvoices: InvoiceRow[] = useMemo(
    () => (selected ? invoices.filter((r) => r.data.designationId === selected.id) : []),
    [invoices, selected],
  );

  const handleSelect = (id: string) => {
    setSelectedId((prev) => (prev === id ? null : id));
  };

  // ── Add / edit via dialog (section, name, order, hoursSold) ───
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingHeader, setEditingHeader] = useState<ExistingDesignation | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const onCreate = () => {
    setEditingHeader(null);
    setDialogOpen(true);
  };

  const onEditHeader = (d: Designation) => {
    setEditingHeader({
      id: d.id,
      section: d.section,
      category: d.category,
      name: d.name,
      hoursSold: d.hoursSold ?? 0,
      catalogId: d.catalogId,
      variant: d.variant,
      isCustom: d.isCustom,
    });
    setDialogOpen(true);
  };

  const handleHeaderSubmit = async (values: DesignationFormValues) => {
    const parsed = parseDesignationForm(values);
    if (editingHeader) {
      await updateDesignationHeader(projectId, editingHeader.id, parsed);
    } else {
      await createDesignationHeader(projectId, parsed);
    }
  };

  const handleHeaderSubmitMultiple = async (multiValues: DesignationFormValues[]) => {
    await createDesignationHeaders(projectId, multiValues.map(parseDesignationForm));
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deleteDesignation(projectId, deleteTarget.id);
      setDeleteTarget(null);
    } finally {
      setBusy(false);
    }
  };

  // Archiving is a one-field write, so it goes through the same partial PUT
  // as the progress quick-edit — the server merges it over the stored doc.
  const handleToggleArchive = async (d: Designation) => {
    await updateDesignationFields(projectId, d.id, { isArchived: !d.isArchived });
    // The row leaves the default view; close the panel so it can't act on a
    // designation the user no longer sees.
    if (archiveFilter !== 'all') setSelectedId(null);
  };

  // ── Invoice dialog + delete confirmation ──────────────────────
  const [invoiceDialog, setInvoiceDialog] = useState<{
    designationId: string;
    designationName: string;
    editing: ExistingInvoice | null;
  } | null>(null);
  const [invoiceDeleteTarget, setInvoiceDeleteTarget] = useState<InvoiceRow | null>(null);
  const [invoiceBusy, setInvoiceBusy] = useState(false);

  const handleInvoiceSubmit = async (values: {
    invoiceNumber: string;
    invoiceDate: Date;
    invoiceAmount: number;
    paymentDate: Date | null;
    type: string;
    currency: string;
  }) => {
    if (!invoiceDialog) return;
    const payload = {
      designationId: invoiceDialog.designationId,
      invoiceNumber: values.invoiceNumber,
      invoiceDate: values.invoiceDate,
      invoiceAmount: values.invoiceAmount,
      ...(values.paymentDate ? { paymentDate: values.paymentDate } : {}),
      type: values.type,
      currency: values.currency,
      updatedAt: serverTimestamp(),
    };
    if (invoiceDialog.editing) {
      await updateInvoice(projectId, invoiceDialog.editing.id, payload);
    } else {
      await createInvoice(projectId, payload);
    }
  };

  const handleInvoiceDelete = async () => {
    if (!invoiceDeleteTarget) return;
    setInvoiceBusy(true);
    try {
      await deleteInvoice(projectId, invoiceDeleteTarget.id);
      setInvoiceDeleteTarget(null);
    } finally {
      setInvoiceBusy(false);
    }
  };

  // ── Inline cell editing (shared by list quick-edit + panel) ───
  const [active, setActive] = useState<ActiveEdit | null>(null);
  const [cellError, setCellError] = useState<string | null>(null);

  const startEdit = (designationId: string, field: EditableField, rawValue: unknown) => {
    const allowed = field === 'progress' ? canWriteFields : canManage;
    if (!allowed) return;
    let stringValue = '';
    if (typeof rawValue === 'number') stringValue = String(rawValue);
    else if (typeof rawValue === 'boolean') stringValue = rawValue ? 'true' : 'false';
    else if (rawValue == null) stringValue = '';
    else stringValue = String(rawValue);
    setActive({ designationId, field, value: stringValue });
    setCellError(null);
  };

  const commitEdit = async () => {
    if (!active) return;
    const { designationId, field, value } = active;
    try {
      if (field === 'progress') {
        const trimmed = value.trim();
        if (trimmed === '') {
          // Empty input clears the declared progress.
          await updateDesignationFields(projectId, designationId, { progress: null });
        } else {
          const n = Number(trimmed);
          if (!Number.isFinite(n) || n < 0 || n > 100) {
            setCellError(t('finance.edit.invalidPercent'));
            return;
          }
          await updateDesignationFields(projectId, designationId, { progress: n });
        }
      } else if (
        field === 'initialOffer' ||
        field === 'orderedAmount' ||
        field === 'hoursSold' ||
        field === 'provision'
      ) {
        const n = Number(value);
        if (!Number.isFinite(n) || n < 0) {
          setCellError(t('finance.edit.invalidNumber'));
          return;
        }
        await updateDesignationFields(projectId, designationId, { [field]: n });
      } else if (field === 'subContracting') {
        await updateDesignationFields(projectId, designationId, {
          subContracting: value === 'true',
        });
      } else if (field === 'status') {
        await updateDesignationFields(projectId, designationId, { status: value });
      } else {
        // String fields: customerPO, altyorPO, comment. Trim + convert
        // empty to null so Firestore doesn't keep stale whitespace.
        const trimmed = value.trim();
        await updateDesignationFields(projectId, designationId, {
          [field]: trimmed === '' ? null : trimmed,
        });
      }
      setActive(null);
      setCellError(null);
    } catch (err) {
      setCellError((err as Error).message ?? 'Save failed');
    }
  };

  const cancelEdit = () => {
    setActive(null);
    setCellError(null);
  };

  const renderCell: RenderCell = (d, field, display, testId) => {
    const isActive = active?.designationId === d.id && active.field === field;
    if (isActive) {
      if (field === 'subContracting') {
        return (
          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
            <Checkbox
              size="small"
              checked={active.value === 'true'}
              onChange={(_, v) => setActive({ ...active, value: v ? 'true' : 'false' })}
            />
            <IconButton size="small" color="primary" onClick={commitEdit}>
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={cancelEdit}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        );
      }
      if (field === 'status') {
        return (
          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
            <TextField
              size="small"
              select
              value={active.value}
              onChange={(e) => setActive({ ...active, value: e.target.value })}
              sx={{ minWidth: { xs: 90, sm: 110 } }}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {t(`finance.status.${s}`)}
                </MenuItem>
              ))}
            </TextField>
            <IconButton size="small" color="primary" onClick={commitEdit}>
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={cancelEdit}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        );
      }
      return (
        <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
          <TextField
            size="small"
            autoFocus
            value={active.value}
            onChange={(e) => setActive({ ...active, value: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitEdit();
              else if (e.key === 'Escape') cancelEdit();
            }}
            error={Boolean(cellError)}
            helperText={cellError ?? ''}
            sx={{ minWidth: { xs: 80, sm: 100 } }}
          />
          <IconButton size="small" color="primary" onClick={commitEdit}>
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={cancelEdit}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      );
    }
    const clickable = field === 'progress' ? canWriteFields : canManage;
    return (
      <Box
        component="span"
        onClick={clickable ? () => startEdit(d.id, field, extractValue(d, field)) : undefined}
        sx={{
          cursor: clickable ? 'pointer' : 'default',
          display: 'inline-block',
        }}
        data-testid={testId ?? `finance-cell-${field}-${d.id}`}
      >
        {display}
      </Box>
    );
  };

  // Progress quick-edit cell in the compact list: mini bar + the same
  // click-to-edit % text as before.
  const renderProgressCell = (d: Designation) => (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
      <LinearProgress
        variant="determinate"
        value={Math.min(d.progress ?? 0, 100)}
        sx={{
          width: 56,
          height: 6,
          flexShrink: 0,
          backgroundColor: colors.lightGray,
          '& .MuiLinearProgress-bar': { backgroundColor: colors.yellow },
        }}
      />
      {renderCell(
        d,
        'progress',
        d.progress != null ? `${formatNumber(d.progress, i18n.language)}%` : '—',
      )}
    </Stack>
  );

  return (
    <Box data-testid="finance-table">
      <Stack
        direction="row"
        spacing={2}
        sx={{
          mb: 2,
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="h3">{t('finance.title')}</Typography>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={archiveFilter}
            onChange={(_, v: ArchiveFilter | null) => v && setArchiveFilter(v)}
            aria-label={t('finance.archive.filterLabel')}
            data-testid="finance-archive-filter"
          >
            {ARCHIVE_FILTERS.map((f) => (
              <ToggleButton
                key={f}
                value={f}
                data-testid={`finance-archive-filter-${f}`}
                sx={{ textTransform: 'none' }}
              >
                {t(`finance.archive.filter.${f}`)}
                {f === 'archived' && archivedCount > 0 ? ` (${archivedCount})` : ''}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {canManage && (
            <Button
              variant="contained"
              color="primary"
              onClick={onCreate}
              data-testid="finance-add-designation"
            >
              {t('projects.designations.new')}
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Sticky project-level KPIs */}
      <FinanceKpiBar summary={summary} locale={i18n.language} />

      {error && (
        <Alert severity="error" sx={{ my: 2 }} data-testid="finance-error">
          {error.message}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : groups.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ p: 4, textAlign: 'center', borderColor: colors.lightGray }}
          data-testid="finance-empty"
        >
          <Typography variant="body2" color="text.secondary">
            {t('projects.designations.empty')}
          </Typography>
        </Paper>
      ) : (
        <Stack direction="row" spacing={2} sx={{ mt: 1, alignItems: 'flex-start' }}>
          <DesignationList
            groups={groups}
            projectId={projectId}
            locale={i18n.language}
            selectedId={selectedId}
            onSelect={handleSelect}
            showInvoiced={Boolean(canSeeInvoices)}
            invoicedByDesignation={invoicedByDesignation}
            renderProgressCell={renderProgressCell}
          />
          {selected && (
            <DesignationDetailPanel
              designation={selected}
              locale={i18n.language}
              rules={rateRows.map((r) => r.data)}
              rateContext={{
                section: selected.section,
                program: project.currentProgram,
                clientIsInternal: Boolean(client?.isInternal),
              }}
              canManage={canManage}
              canArchive={canWriteFields}
              onToggleArchive={() => handleToggleArchive(selected)}
              canSeeInvoices={Boolean(canSeeInvoices)}
              canManageInvoices={canManageInvoices}
              invoices={selectedInvoices}
              renderCell={renderCell}
              onRateOverrideCommit={(v) =>
                updateDesignationFields(projectId, selected.id, {
                  hourlyRateOverride: v === null ? null : v,
                })
              }
              onClose={() => setSelectedId(null)}
              onEditHeader={() => onEditHeader(selected)}
              onDelete={() => setDeleteTarget({ id: selected.id, name: selected.name })}
              onAddInvoice={() =>
                setInvoiceDialog({
                  designationId: selected.id,
                  designationName: selected.name,
                  editing: null,
                })
              }
              onEditInvoice={(inv) =>
                setInvoiceDialog({
                  designationId: selected.id,
                  designationName: selected.name,
                  editing: inv,
                })
              }
              onDeleteInvoice={(inv) => setInvoiceDeleteTarget(inv)}
            />
          )}
        </Stack>
      )}

      {canManage && (
        <DesignationDialog
          open={dialogOpen}
          editing={editingHeader}
          existing={existingForUniqueness}
          onSubmit={handleHeaderSubmit}
          onSubmitMultiple={handleHeaderSubmitMultiple}
          onClose={() => setDialogOpen(false)}
        />
      )}
      <ConfirmDeleteDialog
        open={Boolean(deleteTarget)}
        title={t('projects.designations.deleteTitle')}
        body={t('projects.designations.deleteBody', { name: deleteTarget?.name ?? '' })}
        busy={busy}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
      <ConfirmDeleteDialog
        open={Boolean(invoiceDeleteTarget)}
        title={t('finance.invoicing.deleteTitle')}
        body={t('finance.invoicing.deleteBody', {
          number: invoiceDeleteTarget?.data.invoiceNumber ?? '',
        })}
        busy={invoiceBusy}
        onConfirm={handleInvoiceDelete}
        onClose={() => setInvoiceDeleteTarget(null)}
      />

      <InvoiceDialog
        open={invoiceDialog !== null}
        editing={invoiceDialog?.editing ?? null}
        designationName={invoiceDialog?.designationName ?? ''}
        onSubmit={handleInvoiceSubmit}
        onClose={() => setInvoiceDialog(null)}
      />
    </Box>
  );
}

function extractValue(d: Designation, field: EditableField): unknown {
  switch (field) {
    case 'subContracting':
      return d.subContracting;
    case 'customerPO':
      return d.customerPO ?? '';
    case 'altyorPO':
      return d.altyorPO ?? '';
    case 'comment':
      return d.comment ?? '';
    default:
      return d[field];
  }
}
